import { useState, useEffect } from 'react';
import { FiInfo, FiX, FiExternalLink, FiSave, FiTrash2, FiCamera } from 'react-icons/fi';
import { QRCodeSVG } from 'qrcode.react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { optimizeImage } from '../../utils/imageOptimizer';
import '../../styles/Admin.css';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [customerModalOrder, setCustomerModalOrder] = useState(null);
  const [trackingInputs, setTrackingInputs] = useState({});
  const [qrModalOrder, setQrModalOrder] = useState(null); // The order ID to show QR for

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    try {
      const { data } = await api.get('/orders');
      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Fetch orders failed:', error);
      setOrders([]);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
      toast.success('Status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Update failed');
    }
  };

  const saveTracking = async (orderId) => {
    const trackingNumber = trackingInputs[orderId] || '';

    // Validation: alphanumeric, 10-20 characters for JNT format flexibility
    const isValid = /^[A-Za-z0-9]{10,20}$/.test(trackingNumber);
    if (!isValid) {
      toast.error('Invalid tracking number. Must be 10-20 alphanumeric characters.');
      return;
    }

    try {
      await api.put(`/orders/${orderId}/tracking`, { trackingNumber });
      toast.success('Tracking number saved!');
      fetchOrders();
    } catch (err) {
      toast.error('Failed to save tracking number');
    }
  };

  const deleteOrder = async (id) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        await api.delete(`/orders/${id}`);
        toast.success('Order deleted');
        fetchOrders();
      } catch (error) {
        toast.error('Delete failed');
      }
    }
  };

  // Format order ID from date
  const formatOrderId = (createdAt) => {
    const d = new Date(createdAt);
    const year = d.getFullYear();
    const mmdd = `${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    const hhmm = `${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
    return `${year}-${mmdd}-${hhmm}`;
  };

  // Format date for display
  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if tracking should be editable (shipped or higher)
  const canEditTracking = (status) => {
    return ['shipped', 'delivered'].includes(status);
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />

      <main className="admin-main">
        <h1>Manage Orders</h1>

        <div className="orders-table-container">
          <table className="admin-table orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer's Details</th>
                <th>Items</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Tracking Number</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order, index) => (
                <tr key={order._id} className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                  {/* Order ID - No Wrap */}
                  <td className="order-id-cell" data-label="Order ID">
                    #{formatOrderId(order.createdAt)}
                  </td>

                  {/* Customer Details - Expandable */}
                  <td className="customer-cell" data-label="Customer">
                    <div className="customer-summary">
                      <div className="customer-name-group">
                        <strong>{order.user?.name || order.shippingAddress?.fullName || 'Guest'}</strong>
                        <small>{order.user?.email || 'N/A'}</small>
                      </div>
                      <button
                        className="info-toggle-btn mobile-icon-only"
                        onClick={() => setCustomerModalOrder(order)}
                        aria-label="View customer details"
                        title="View Full Details"
                      >
                        <FiInfo />
                      </button>
                    </div>
                  </td>

                  {/* Items */}
                  <td className="items-cell" data-label="Items">
                    <div className="items-list">
                      {(order.items || []).map((item, idx) => (
                        <div key={idx} className="item-row">
                          <img
                            src={optimizeImage(item.product?.images?.[0] || '/placeholder.jpg', 60)}
                            alt=""
                            className="item-thumb"
                          />
                          <span>{item.product?.name} (x{item.quantity})</span>
                        </div>
                      ))}
                    </div>
                  </td>

                  {/* Total */}
                  <td className="total-cell" data-label="Total">
                    ₱{(order.total || 0).toFixed(2)}
                  </td>

                  {/* Payment */}
                  <td className="payment-cell" data-label="Payment">
                    <span className="payment-method">{order.paymentMethod?.toUpperCase()}</span>
                    {order.paymentProof && (
                      <a
                        href={order.paymentProof}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-proof-link"
                      >
                        View Proof
                      </a>
                    )}
                  </td>

                  {/* Status */}
                  <td className="status-cell" data-label="Status">
                    <span className={`status-badge-orders ${order.status}`}>
                      {order.status === 'awaiting_payment_verification' ? 'Awaiting Verification' : order.status}
                    </span>
                    {order.paymentMethod === 'gcash' && order.status === 'awaiting_payment_verification' && (
                      <a href="/admin/payments" className="verify-shortcut-link" style={{ display: 'block', fontSize: '0.75rem', color: '#C41E3A', marginTop: '4px', fontWeight: '700' }}>
                        Verify in Payments →
                      </a>
                    )}
                  </td>

                  {/* Tracking Number / Delivery - Dedicated Column */}
                  <td className="tracking-cell" data-label="Tracking">
                    {order.shippingMethod === 'inhouse' ? (
                      <div className="inhouse-delivery-actions">
                        <span className="shipping-badge inhouse">In-House</span>
                        <button
                          className="generate-qr-btn"
                          onClick={() => setQrModalOrder(order)}
                          title="Generate Rider QR Code"
                        >
                          Show Rider QR
                        </button>
                        {order.deliveryProof && (
                          <a href={order.deliveryProof} target="_blank" rel="noopener noreferrer" className="view-proof-link" style={{ display: 'block', marginTop: '5px' }}>
                            View Delivery Photo
                          </a>
                        )}
                      </div>
                    ) : ( 
                      canEditTracking(order.status) ? (
                        order.trackingNumber ? (
                          <a
                            href={`https://www.jntexpress.ph/tracking?tracking_number=${order.trackingNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tracking-link"
                          >
                            {order.trackingNumber}
                            <FiExternalLink />
                          </a>
                        ) : (
                          <div className="tracking-input-group">
                            <input
                              type="text"
                              className="tracking-input"
                              placeholder="Enter J&T tracking #"
                              value={trackingInputs[order._id] || ''}
                              onChange={(e) => setTrackingInputs(prev => ({
                                ...prev,
                                [order._id]: e.target.value.toUpperCase()
                              }))}
                              maxLength={20}
                            />
                            <button
                              className="save-tracking-btn"
                              onClick={() => saveTracking(order._id)}
                              title="Save Tracking"
                            >
                              <FiSave />
                            </button>
                          </div>
                        )
                      ) : (
                        <span className="tracking-na">—</span>
                      )
                    )}
                  </td>

                  {/* Action */}
                  <td className="action-cell" data-label="Action">
                    <select
                      value={order.status}
                      onChange={(e) => updateStatus(order._id, e.target.value)}
                      className="status-select"
                    >
                      <option value="pending">Pending</option>
                      <option value="awaiting_payment_verification">Awaiting Verification</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="rejected">Rejected</option>
                    </select>

                    <button
                      onClick={() => deleteOrder(order._id)}
                      className="delete-order-btn"
                      title="Delete Order"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {orders.length === 0 && (
          <div className="no-orders-message">
            <p>No orders found.</p>
          </div>
        )}

        {/* Customer Details Modal */}
        {customerModalOrder && (
          <div className="qr-modal-overlay" onClick={() => setCustomerModalOrder(null)}>
            <div className="qr-modal-content customer-modal" onClick={e => e.stopPropagation()}>
              <button className="close-modal-btn" onClick={() => setCustomerModalOrder(null)}>
                <FiX />
              </button>
              <h2>Customer Details</h2>
              <div className="customer-details-card modal-view">
                <div className="details-grid">
                  <div className="detail-row">
                    <span className="detail-label">Full Name:</span>
                    <span>{customerModalOrder.shippingAddress?.fullName || customerModalOrder.user?.name || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Email:</span>
                    <span>{customerModalOrder.user?.email || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Phone:</span>
                    <span>{customerModalOrder.shippingAddress?.phone || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Shipping Address:</span>
                    <span>
                      {customerModalOrder.shippingAddress ? (
                        <>
                          {customerModalOrder.shippingAddress.street}, {customerModalOrder.shippingAddress.city}<br />
                          {customerModalOrder.shippingAddress.province}, {customerModalOrder.shippingAddress.postalCode}<br />
                          {customerModalOrder.shippingAddress.country || 'Philippines'}
                        </>
                      ) : 'N/A'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Order Date:</span>
                    <span>{formatDate(customerModalOrder.createdAt)}</span>
                  </div>
                </div>
              </div>
              <button className="modal-close-action-btn" onClick={() => setCustomerModalOrder(null)}>
                Close
              </button>
            </div>
          </div>
        )}

        {/* QR Code Modal for Riders */}
        {qrModalOrder && (
          <div className="qr-modal-overlay" onClick={() => setQrModalOrder(null)}>
            <div className="qr-modal-content" onClick={e => e.stopPropagation()}>
              <button className="close-modal-btn" onClick={() => setQrModalOrder(null)}>
                <FiX />
              </button>
              <h2>In-House Delivery QR Code</h2>
              <p>Scan this code with a mobile phone to view delivery details and upload a photo.</p>

              <div className="qr-wrapper">
                <QRCodeSVG
                  value={`${window.location.origin}/rider/order/${qrModalOrder._id}`}
                  size={256}
                  level={"H"}
                  includeMargin={true}
                />
              </div>

              <div className="qr-modal-details">
                <p><strong>Order ID:</strong> #{formatOrderId(qrModalOrder.createdAt)}</p>
                <p><strong>Customer:</strong> {qrModalOrder.shippingAddress?.fullName || 'N/A'}</p>
                <p><strong>COD Amount:</strong> {qrModalOrder.paymentMethod === 'cod' ? `₱${qrModalOrder.total?.toFixed(2)}` : 'PAID (GCash)'}</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminOrders;